import {
	Component, Input, Output, EventEmitter, OnChanges, SimpleChanges,
	ChangeDetectorRef, ChangeDetectionStrategy, SimpleChange
} from '@angular/core';
import { ComponentBase } from 'common';
import { DiscResultsService, ExportToPrtgOptions } from 'discovery/services/discresults.service';
import { PrtgSettingsSummaryDto } from 'UvExplorerServer.Dtos.TS/Session/PrtgSettingsSummaryDto';

// Dialog for the map-level "Export to PRTG ..." context-menu action. Tabs
// mirror the WinForms ExportToPrtgForm: Group, Map, SNMP Sensors, WMI Sensors.
// Defaults match the WinForms form's out-of-the-box settings; nothing is
// persisted between dialog opens.
@Component({
	selector: 'export-to-prtg',
	templateUrl: './export-to-prtg.component.html',
	styleUrls: ['./export-to-prtg.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportToPrtgComponent extends ComponentBase implements OnChanges {

	@Input() public display: boolean;
	@Input() public netViewGuid: string;
	@Input() public defaultExportName: string;

	@Output() public displayChange = new EventEmitter<boolean>();
	@Output() public onCancel = new EventEmitter<void>();
	@Output() public onExport = new EventEmitter<{
		prtgSettingsGuid: string,
		exportName: string,
		options: ExportToPrtgOptions
	}>();

	public dropdownOptions: Array<{ label: string, value: string, disabled: boolean }> = [];
	public selectedGuid: string = "";
	public exportName: string = "";
	public loading: boolean = false;
	public errorMessage: string = "";

	// Read-only summary line shown under the PRTG server dropdown — matches
	// the "Summary: Server URL: ...; Username: ..." line in the WinForms form.
	// PrtgSettingsSummaryDto doesn't carry a username so the URL is all we show.
	public serverSummary: string = "";

	private summariesByGuid: { [guid: string]: PrtgSettingsSummaryDto } = {};

	// Tab models — bound directly to the form controls. Defaults match the
	// WinForms form's out-of-the-box state.
	public options: ExportToPrtgOptions = this.buildDefaultOptions();

	// Display label for the chosen Parent Group (the readonly input next to
	// the Browse... button). Kept in sync with options.parentGroupId.
	public parentGroupLabel: string = "";

	// Picker dialog state. A single PrtgGroupPickerComponent instance is reused
	// for both Browse... buttons; pickerMode tracks which one opened it so the
	// onSelect handler routes the result correctly.
	public pickerVisible: boolean = false;
	public pickerTitle: string = "";
	public pickerMode: "parent" | "group" = "parent";

	constructor(
		private discResultsService: DiscResultsService,
		private changeDetector: ChangeDetectorRef) {
		super();
		this.display = false;
		this.netViewGuid = "";
		this.defaultExportName = "";
	}

	public ngOnChanges(changes: SimpleChanges): void {
		const displayChange: SimpleChange = changes["display"];
		if (displayChange && displayChange.currentValue === true && this.netViewGuid) {
			this.exportName = this.defaultExportName || "";
			this.options = this.buildDefaultOptions();
			this.parentGroupLabel = "";
			this.loadPrtgSettings();
		}
	}

	private buildDefaultOptions(): ExportToPrtgOptions {
		return {
			parentGroupId: 0,

			createNewGroup: true,
			createDependency: false,
			removeMissingDevices: false,
			exportPrtgDevicesOnly: false,
			exportMapOnly: false,
			mergeMapOnExport: false,
			exportMasterNetworkOnly: false,
			useTemplateTags: false,

			createTopologyMap: true,
			overwriteExistingTopologyMap: false,

			addPingSensors: true,
			addSnmpUpTimeSensors: false,
			addSnmpCpuSensors: false,
			addSnmpMemorySensors: false,
			addSnmpDiskFreeSensors: false,
			addSnmpTrafficSensors: false,
			useSnmpIfName: false,
			configAllInterfacesIfUp: false,
			configAllPhysicalInterfaces: false,
			configAllUpLinkInterfaces: false,
			snmpIfSensorFormat: "",
			configAllApSensors: false,

			addWmiUpTimeSensors: false,
			addWmiCpuSensors: false,
			addWmiDiskSpaceSensors: false,
			addWmiMemorySensors: false,
		};
	}

	private loadPrtgSettings(): void {
		this.loading = true;
		this.errorMessage = "";
		this.dropdownOptions = [];
		this.selectedGuid = "";
		this.summariesByGuid = {};
		this.serverSummary = "";
		this.changeDetector.markForCheck();

		this.discResultsService.listPrtgSettings(this.netViewGuid).subscribe(
			(summaries: PrtgSettingsSummaryDto[]) => {
				this.loading = false;
				if (!summaries || summaries.length === 0) {
					this.errorMessage = "No PRTG settings are configured for this network. Add one in Protocol Settings first.";
				} else {
					this.dropdownOptions = summaries.map(s => ({
						label: this.formatLabel(s),
						value: s.prtg_settings_guid,
						disabled: false,
					}));
					this.summariesByGuid = {};
					for (const s of summaries) this.summariesByGuid[s.prtg_settings_guid] = s;
					this.selectedGuid = this.dropdownOptions[0].value;
					this.updateServerSummary();
				}
				this.changeDetector.markForCheck();
			},
			(error) => {
				this.loading = false;
				this.errorMessage = "Failed to load PRTG agents.";
				this.changeDetector.markForCheck();
			});
	}

	public onPrtgSettingsChanged(): void {
		this.updateServerSummary();
		this.changeDetector.markForCheck();
	}

	private updateServerSummary(): void {
		const s = this.summariesByGuid[this.selectedGuid];
		if (!s) {
			this.serverSummary = "";
			return;
		}
		const url = s.server_url || "(no URL)";
		this.serverSummary = "Server URL: " + url + (s.is_hosted_prtg ? " (hosted PRTG)" : "");
	}

	private formatLabel(s: PrtgSettingsSummaryDto): string {
		let base = s.name || s.server_url || "(unnamed PRTG agent)";
		if (s.is_hosted_prtg) base += " — hosted PRTG";
		return base;
	}

	public get enableExport(): boolean {
		return !this.loading && !!this.selectedGuid && !!this.exportName && this.exportName.trim().length > 0;
	}

	public hide(): void {
		this.display = false;
		this.displayChange.emit(this.display);
		this.changeDetector.markForCheck();
	}

	public cancel(): void {
		this.hide();
		this.onCancel.emit();
	}

	public export(): void {
		if (!this.enableExport) return;
		const chosenGuid = this.selectedGuid;
		const chosenName = this.exportName.trim();
		const chosenOptions = { ...this.options, snmpIfSensorFormat: (this.options.snmpIfSensorFormat || "").trim() };
		this.hide();
		this.onExport.emit({
			prtgSettingsGuid: chosenGuid,
			exportName: chosenName,
			options: chosenOptions,
		});
	}

	// ── Browse... pickers ─────────────────────────────────────────────────

	public browseParentGroup(): void {
		if (!this.selectedGuid) return;
		this.pickerMode = "parent";
		this.pickerTitle = "Select Parent Group";
		this.pickerVisible = true;
	}

	public browseGroupName(): void {
		if (!this.selectedGuid) return;
		this.pickerMode = "group";
		this.pickerTitle = "Select PRTG Group";
		this.pickerVisible = true;
	}

	public clearParentGroup(): void {
		this.options.parentGroupId = 0;
		this.parentGroupLabel = "";
		this.changeDetector.markForCheck();
	}

	public onGroupPicked(event: { id: number, name: string }): void {
		if (this.pickerMode === "parent") {
			this.options.parentGroupId = event.id;
			this.parentGroupLabel = event.name + " [" + event.id + "]";
		} else {
			// "Browse..." next to Group Name: prefill the input with the
			// selected group's name. This is the WinForms behavior — picking
			// an existing group means "update/overwrite that group".
			this.exportName = event.name;
		}
		this.changeDetector.markForCheck();
	}
}
